import {DialogFragment, PageFragment, PageFragmentProps, PageFragmentState} from "shared/PageFragment";
import {TextField, Typography} from "@mui/material";
import {DW_LG, PD_XLG2} from "shared/dimens";
import {App} from "app/App";
import {DIALOG_FLAG_DISABLE_BACKDROP_CLICK, DIALOG_FLAG_SHOW_CLOSE} from "shared/BaseApp";
import React from "react";
import {
  SiteDomain as SiteDomain,
  SiteDomains as SiteDomains
} from "@/vantage-shared/types";
import {PracticeData} from "@/libwyze-shared/types";
import {StyledBoxColumn, StyledBoxRow} from "@/shared/StyledComponents";
import {VantageApi} from "@/vantage-shared/vantage_api";

export class EditSiteDomainHelper {

  static addSiteDomain(siteId: string) {
    const id = App.CONTEXT.showDialog(
      {flags: DIALOG_FLAG_SHOW_CLOSE | DIALOG_FLAG_DISABLE_BACKDROP_CLICK},
      () => <CreateSiteDomainFragment siteId={siteId} onSaveSiteDomain={name => {
        App.CONTEXT.hideDialog(id);
        setTimeout(() => {
          App.CONTEXT.showProgressDialog("Please wait...", null, async function () {
            const practiceId = PracticeData.provider().practiceId;
            try {
              if (!await VantageApi.getInstance().requestVantageSiteCustomDomainAdd(practiceId, siteId, name)) {
                throw new Error("Error adding custom domain");
              }
              const item = SiteDomain.createCustom(siteId);
              item.name = name;
              await new SiteDomains(practiceId).addListItem(item);
            } catch (e) {
              App.CONTEXT.showToast("Oops, something went wrong. Please try again later.");
            }
          });
        }, 100);
      }}/>);
  }

  static deleteSiteDomain(siteDomain: SiteDomain) {
    const id = App.CONTEXT.showConfirmDialog("Are you sure you want to delete this domain? This action cannot be undone", () => {
      App.CONTEXT.hideDialog(id);
      setTimeout(() => {
        App.CONTEXT.showProgressDialog("Please wait...", null, async function () {
          const practiceId = PracticeData.provider().practiceId;
          await VantageApi.getInstance().requestVantageSiteCustomDomainDelete(practiceId, siteDomain.id, siteDomain.name)
          await new SiteDomains(practiceId).deleteListItem(siteDomain);
        });
      }, 100);
    });
  }
}

type CreateSiteDomainFragmentProps = PageFragmentProps & {
  siteId: string,
  onSaveSiteDomain: (name: string) => void,
}

type CreateSiteDomainFragmentState = PageFragmentState & {
  domain?: string,
}

class CreateSiteDomainFragment extends DialogFragment<CreateSiteDomainFragmentProps, CreateSiteDomainFragmentState> {

  private readonly siteDomains = new SiteDomains(PracticeData.provider().practiceId);

  protected onCreateState() {
    return {
      ...super.onCreateState(),
      title: "Add Custom Domain",
    };
  }

  componentDidMount() {
    super.componentDidMount();
    this.setActionButtonEnabled(false);
  }

  protected styleFlags(): number {
    return PageFragment.STYLE_ACTION_PANEL_TYPE_BUTTON_FLAG;
  }

  protected getActionButtonText(): string {
    return "Add";
  }

  onActionButtonClicked() {
    this.props.onSaveSiteDomain(`www.${this.state.domain}`);
  }

  renderContent(): React.ReactElement | null {
    return <StyledBoxColumn style={{width: DW_LG, padding: PD_XLG2, gap: PD_XLG2}}>
      <Typography>
        Enter the exact domain name you want people to see when they visit your site.
      </Typography>
      <StyledBoxRow style={{width: "100%", alignItems: "center"}}>
        <Typography variant={"h5"}>www.</Typography>
        <TextField
          size={"small"}
          style={{flexGrow: 1}}
          variant={"outlined"}
          placeholder={"Domain (e.g mydentalclinic.com)"}
          value={this.state.domain}
          onChange={event => {
            this.setActionButtonEnabled(Boolean(this.state.domain))
            this.setState({domain: event.target.value});
          }}/>
      </StyledBoxRow>
    </StyledBoxColumn>;
  }
}
